<template lang="html">
  <!-- страница заведена в CMS (слаг `faq`) — показываем её вместо шаблонной -->
  <div
    v-if="hasCmsContent"
    class="w-full max-w-(--ui-container) px-4 py-8 sm:m-auto sm:py-10"
  >
    <h1 class="mb-6 text-2xl font-bold">
      {{ item!.title }}
    </h1>
    <InfoCmsBody :item="item" :has-cms-content="hasCmsContent" />
  </div>

  <div v-else class="w-full max-w-(--ui-container) px-4 py-8 sm:m-auto sm:py-10">
    <h1 class="mb-6 text-2xl font-bold">
      FAQ
    </h1>

    <div class="flex flex-col gap-8">
      <section>
        <h2 class="mb-3 text-xl font-semibold">
          Одежда
        </h2>

        <UAccordion :items="clothingItems" type="multiple" :ui="accordionUi">
          <template #delivery>
            <div class="text-sm leading-6 text-toned">
              <p>
                Отправка вещей в наличии осуществляется в течение 5 рабочих дней после заказа.
              </p>
              <p class="mt-3">
                Время доставки занимает от 3 до 14 дней, в зависимости от удалённости адресата от г. {{ brandInfo.city }}.
              </p>
              <p class="mt-3">
                Трек-номер предоставляется на эл. почту. По трек-номеру вы можете отследить посылку на сайте Почты России или СДЭК:
                <a class="text-primary hover:underline" href="https://gdeposylka.ru" target="_blank" rel="noopener noreferrer">gdeposylka.ru</a>.
              </p>
              <p class="mt-3">
                В г. {{ brandInfo.city }} возможна отправка курьером или самовывоз (при наличии).
              </p>
              <p class="mt-3">
                Стоимость доставки рассчитывается в корзине.
              </p>
            </div>
          </template>

          <template #returns>
            <div class="text-sm leading-6 text-toned">
              <p>
                Для возврата или обмена товара надлежащего качества направьте обращение на
                <a class="text-primary hover:underline" :href="`mailto:${brandInfo.email}`">{{ brandInfo.email }}</a>
                с пометкой «обмен/возврат товара надлежащего качества».
              </p>
              <p class="mt-3">
                Возврат и обмен товара надлежащего качества возможен в течение 14 календарных дней с даты получения при сохранении товарного вида и потребительских свойств.
              </p>
              <p class="mt-3">
                Возврат товара ненадлежащего качества оформляется через обращение на
                <a class="text-primary hover:underline" :href="`mailto:${brandInfo.email}`">{{ brandInfo.email }}</a>
                с пометкой «товар ненадлежащего качества».
              </p>
              <p class="mt-3">
                При возврате товара надлежащего качества доставка до склада продавца оплачивается покупателем.
              </p>
              <p class="mt-3">
                Полный порядок возврата описан в
                <NuxtLink class="text-primary hover:underline" to="/returns">
                  Политике возвратов
                </NuxtLink>.
              </p>
            </div>
          </template>

          <template #care>
            <div class="text-sm leading-6 text-toned">
              <h3 class="mb-2 font-semibold">
                Для одежды из хлопка
              </h3>
              <ul class="list-disc space-y-1 pl-5">
                <li>При автоматической стирке включайте режим «Деликатная стирка».</li>
                <li>Не рекомендуется температура выше 30 градусов.</li>
                <li>Гладить с изнаночной стороны или через специальную ткань.</li>
                <li>Избегайте плотного отжима (не рекомендуется больше 800 оборотов).</li>
                <li>Перед стиркой вывернуть вещь наизнанку (кроме вещей с начесом).</li>
                <li>Изделия с вышивкой рекомендуется стирать отдельно от другого белья.</li>
                <li>Используйте мягкие моющие средства.</li>
                <li>Не добавляйте отбеливающие средства с оптическими добавками.</li>
              </ul>

              <h3 class="mt-5 mb-2 font-semibold">
                Для одежды из синтетики
              </h3>
              <ul class="list-disc space-y-1 pl-5">
                <li>При автоматической стирке включайте режим «Синтетика».</li>
                <li>Гладить с изнаночной стороны или через специальную ткань.</li>
                <li>Избегайте плотного отжима.</li>
                <li>Используйте качественное, желательно жидкое, средство для стирки.</li>
              </ul>

              <h3 class="mt-5 mb-2 font-semibold">
                Для шерстяной одежды
              </h3>
              <ul class="list-disc space-y-1 pl-5">
                <li>Только ручная стирка.</li>
                <li>Стирать в холодной или теплой воде не выше 30 градусов.</li>
              </ul>
            </div>
          </template>
        </UAccordion>
      </section>

      <section>
        <h2 class="mb-3 text-xl font-semibold">
          Контакты
        </h2>
        <div class="rounded-none border border-default p-4 text-sm leading-6 text-toned">
          <p>
            По вопросам магазина (возврат, сотрудничество) напишите нам удобным способом.
          </p>
          <p v-if="brandInfo.vkName" class="mt-3">
            VK:
            <a
              class="text-primary hover:underline"
              :href="`https://vk.com/${brandInfo.vkName}`"
              target="_blank"
              rel="noopener noreferrer"
            >vk.com/{{ brandInfo.vkName }}</a>
          </p>
          <p v-if="brandInfo.tgName">
            TG:
            <a
              class="text-primary hover:underline"
              :href="`https://t.me/${brandInfo.tgName}`"
              target="_blank"
              rel="noopener noreferrer"
            >t.me/{{ brandInfo.tgName }}</a>
          </p>
          <p>
            Mail:
            <a class="text-primary hover:underline" :href="`mailto:${brandInfo.email}`">{{ brandInfo.email }}</a>
          </p>
          <p class="mt-3">
            {{ brandInfo.legalName }}
          </p>
          <p>
            ИНН: {{ brandInfo.inn }}
          </p>
        </div>
      </section>

      <section>
        <h2 class="mb-3 text-xl font-semibold">
          Документы
        </h2>

        <UAccordion :items="legalItems" type="multiple" :ui="accordionUi">
          <template #legal-contacts>
            <div class="text-sm leading-6 text-toned">
              <p>
                По вопросам магазина: возврат, сотрудничество — напишите нам.
              </p>
              <p v-if="brandInfo.vkName" class="mt-3">
                VK:
                <a
                  class="text-primary hover:underline"
                  :href="`https://vk.com/${brandInfo.vkName}`"
                  target="_blank"
                  rel="noopener noreferrer"
                >vk.com/{{ brandInfo.vkName }}</a>
              </p>
              <p v-if="brandInfo.tgName">
                TG:
                <a
                  class="text-primary hover:underline"
                  :href="`https://t.me/${brandInfo.tgName}`"
                  target="_blank"
                  rel="noopener noreferrer"
                >t.me/{{ brandInfo.tgName }}</a>
              </p>
              <p>
                Mail:
                <a class="text-primary hover:underline" :href="`mailto:${brandInfo.email}`">{{ brandInfo.email }}</a>
              </p>
            </div>
          </template>

          <template #public-offer>
            <p class="text-sm leading-6 text-toned">
              <NuxtLink class="text-primary hover:underline" to="/offer">
                Публичная оферта
              </NuxtLink>
            </p>
          </template>

          <template #privacy-policy>
            <p class="text-sm leading-6 text-toned">
              <NuxtLink class="text-primary hover:underline" to="/privacy">
                Политика конфиденциальности
              </NuxtLink>
            </p>
          </template>

          <template #consent>
            <p class="text-sm leading-6 text-toned">
              <NuxtLink class="text-primary hover:underline" to="/agreement">
                Согласие на обработку персональных данных
              </NuxtLink>
            </p>
          </template>

          <template #data-transfer>
            <p class="text-sm leading-6 text-toned">
              <NuxtLink class="text-primary hover:underline" to="/data-transfer">
                Согласие на передачу персональных данных третьим лицам
              </NuxtLink>
            </p>
          </template>

          <template #returns-policy>
            <p class="text-sm leading-6 text-toned">
              <NuxtLink class="text-primary hover:underline" to="/returns">
                Политика возвратов товара
              </NuxtLink>
            </p>
          </template>
        </UAccordion>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { AccordionItem } from '@nuxt/ui'

import { brandInfo } from '#Brand/info'
import InfoCmsBody from '#Info/components/InfoCmsBody.vue'
import { useInfoCmsPage } from '#Info/composables/useInfoCmsPage'
import { infoCmsSlugs } from '#Info/constants/cmsPages'

defineOptions({
  name: 'InfoFaqPage',
})

const { item, hasCmsContent } = await useInfoCmsPage(infoCmsSlugs.faq)

useContentSeo(item, {
  fallbackTitle: 'FAQ',
  fallbackDescription: `Частые вопросы о доставке, возврате и уходе за вещами ${brandInfo.name}.`,
})

const accordionUi = {
  root: 'border border-default rounded-none',
  item: 'border-b border-default last:border-b-0',
  trigger: 'px-4 py-3 text-sm font-medium',
  content: 'px-4 pb-4',
}

const clothingItems: AccordionItem[] = [
  { label: 'Доставка', value: 'delivery', slot: 'delivery' },
  { label: 'Возврат', value: 'returns', slot: 'returns' },
  { label: 'Уход', value: 'care', slot: 'care' },
]

const legalItems: AccordionItem[] = [
  { label: 'Контакты', value: 'legal-contacts', slot: 'legal-contacts' },
  { label: 'Публичная оферта', value: 'public-offer', slot: 'public-offer' },
  { label: 'Политика конфиденциальности', value: 'privacy-policy', slot: 'privacy-policy' },
  { label: 'Согласие на обработку персональных данных', value: 'consent', slot: 'consent' },
  { label: 'Согласие на передачу персональных данных третьим лицам', value: 'data-transfer', slot: 'data-transfer' },
  { label: 'Политика возвратов товара', value: 'returns-policy', slot: 'returns-policy' },
]
</script>
